import { memo, useRef } from 'react'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import BodyText from 'LuxKit/Typography/BodyText'
import SearchFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/SearchFloatingPanel'
import VerticalDateRangePicker from 'components/Common/Calendar/VerticalDateRangePicker'
import FlexibleDatePicker from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker'
import SearchDateToggle from 'components/Search/SearchForm/SearchDateInput/SearchDateToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FilterChipsScrollContainer from 'tripPlanner/components/TripItemFilters/FilterChipsScrollContainer'
import Group from 'components/utils/Group'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import type { ChatHotelCarouselFiltersProps } from './ChatHotelCarouselFilters.types'

interface Props extends ChatHotelCarouselFiltersProps {}

function ChatHotelCarouselMobileFilters({
  cardType,
  canShowOccupancyFilter,
  canShowDateFilter,
  filterActive,
  occupancy,
  dateFilter,
  focusMonth,
  applyLabel,
  occupancyPanelTitle,
  datePanelTitle,
  clearFiltersLabel,
  onClearFilters,
}: Props) {
  const datePanelBodyRef = useRef<HTMLDivElement>(null)

  const occupancyPickerFooter = (
    <Group direction="horizontal" horizontalAlign="end">
      <BodyText variant="large">
        <FormattedOccupancy occupancies={occupancy.pendingRooms} />
      </BodyText>
    </Group>
  )

  const dateFilterForm = (
    <Group direction="vertical" gap={24}>
      <Group direction="horizontal" horizontalAlign="center">
        <SearchDateToggle
          onSelectSpecific={dateFilter.handleSelectSpecific}
          onSelectFlexible={dateFilter.handleSelectFlexible}
          onSelectAnytime={dateFilter.handleSelectAnytime}
          anytimeDateSelected={dateFilter.pendingDateMode === DATE_SEARCH_OPTION_IDS.ANYTIME}
          dateSearchOptionId={dateFilter.pendingDateMode}
        />
      </Group>
      {dateFilter.shouldShowFlexiblePicker && (
        <FlexibleDatePicker
          onDurationChange={dateFilter.handlePendingFlexibleNightsChange}
          onMonthChange={dateFilter.handlePendingFlexibleMonthsChange}
          previouslySelectedDuration={dateFilter.pendingFlexibleNights}
          previouslySelectedMonths={dateFilter.pendingFlexibleMonths || undefined}
        />
      )}
      {dateFilter.shouldShowSpecificPicker && (
        <VerticalDateRangePicker
          startDate={dateFilter.pendingCheckIn}
          endDate={dateFilter.pendingCheckOut}
          onDatesChange={dateFilter.handlePendingDatesChange}
          min={dateFilter.today}
          max={dateFilter.calendarMaxDate}
          contentRef={datePanelBodyRef}
          allowSameDayRange={cardType === 'experience'}
          focusMonth={focusMonth}
        />
      )}
    </Group>
  )

  return (
    <FilterChipsScrollContainer>
      {canShowOccupancyFilter && (
        <>
          <AssistChip
            ref={occupancy.triggerRef}
            variant="outline"
            size="medium"
            endIcon={<DropdownChevron open={occupancy.open} />}
            onClick={occupancy.handleToggle}
          >
            <FormattedOccupancy occupancies={occupancy.rooms} />
          </AssistChip>
          <SearchFloatingPanel
            title={occupancyPanelTitle}
            isOpen={occupancy.open}
            onClose={occupancy.handleClose}
            onGoBack={occupancy.handleClose}
            onConfirm={occupancy.handleApply}
            confirmText={applyLabel}
            footerContent={occupancyPickerFooter}
          >
            <OccupancyPicker
              occupancies={occupancy.pendingRooms}
              onChange={occupancy.handlePendingChange}
              enableMultiRoom
              childVariant="child-age"
            />
          </SearchFloatingPanel>
        </>
      )}
      {canShowDateFilter && (
        <>
          <AssistChip
            ref={dateFilter.triggerRef}
            variant="outline"
            size="medium"
            endIcon={<DropdownChevron open={dateFilter.open} />}
            onClick={dateFilter.handleToggle}
          >
            {dateFilter.chipLabel}
          </AssistChip>
          <SearchFloatingPanel
            ref={datePanelBodyRef}
            title={datePanelTitle}
            isOpen={dateFilter.open}
            onClose={dateFilter.handleClose}
            onGoBack={dateFilter.handleClose}
            onConfirm={dateFilter.handleApply}
            confirmText={applyLabel}
            confirmButtonDisabled={dateFilter.isApplyDisabled}
          >
            <VerticalSpacer gap={24}>
              {dateFilterForm}
            </VerticalSpacer>
          </SearchFloatingPanel>
        </>
      )}
      {filterActive && (
        <AssistChip
          variant="filled"
          size="medium"
          endIcon={<LineTimesIcon />}
          onClick={onClearFilters}
        >
          {clearFiltersLabel}
        </AssistChip>
      )}
    </FilterChipsScrollContainer>
  )
}

export default memo(ChatHotelCarouselMobileFilters)
